import { defineStore } from 'pinia';
import { shallowRef } from 'vue';
import { saveAgentConversation } from '@/api/agentApi';
import { createProjectRequestId } from '@/api/projectApi';
import {
  buildProjectCreateWordSnapshotHtml,
  type ProjectCreateDraftSnapshotV1,
  type ProjectCreateFormSnapshot,
} from '@/utils/projectCreateWordSnapshot';

const LS_PREFIX = 'aipd_pcreate_draft_v1_';
const LS_INDEX = 'aipd_pcreate_draft_index_v1';

type DraftIndexRow = {
  draftKey: string;
  sessionId: string;
  updatedAt: number;
  titleHint: string;
};

function newDraftKey(): string {
  try {
    return `d_${crypto.randomUUID().replace(/-/g, '').slice(0, 16)}`;
  } catch {
    return `d_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
  }
}

function emptyForm(): ProjectCreateFormSnapshot {
  return {
    name: '',
    projectCode: '',
    owner: '',
    description: '',
    projectDeliveryDate: '',
    projectLeadUserId: undefined,
    projectMemberUserIds: [],
  };
}

function emptySnapshot(draftKey: string): ProjectCreateDraftSnapshotV1 {
  return {
    version: 1,
    draftKey,
    sessionId: '',
    createRequestId: createProjectRequestId(),
    updatedAt: Date.now(),
    form: emptyForm(),
    phasePlans: [],
    aiStructuredResult: null,
    aiProjectGoal: '',
    aiProjectType: '',
    aiProjectRisks: [],
  };
}

function readIndex(): DraftIndexRow[] {
  try {
    const raw = localStorage.getItem(LS_INDEX);
    if (!raw) return [];
    const arr = JSON.parse(raw) as DraftIndexRow[];
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

function writeIndex(rows: DraftIndexRow[]) {
  try {
    localStorage.setItem(LS_INDEX, JSON.stringify(rows.slice(0, 200)));
  } catch {
    /* ignore */
  }
}

function upsertIndexRow(row: DraftIndexRow) {
  const rows = readIndex().filter((r) => r.draftKey !== row.draftKey && r.sessionId !== row.sessionId);
  rows.unshift(row);
  writeIndex(rows);
}

export const useProjectCreateDraftStore = defineStore('projectCreateDraft', () => {
  /** 当前编辑中的草稿（内存），按 draftKey */
  const working = shallowRef<Record<string, ProjectCreateDraftSnapshotV1>>({});

  function getWorking(draftKey: string): ProjectCreateDraftSnapshotV1 | null {
    if (!draftKey) return null;
    return working.value[draftKey] ?? null;
  }

  function ensureDraft(draftKey: string): ProjectCreateDraftSnapshotV1 {
    let s = working.value[draftKey];
    if (!s) {
      const loaded = loadPersistedDraft(draftKey);
      s = loaded ?? emptySnapshot(draftKey);
      working.value = { ...working.value, [draftKey]: s };
    }
    return s;
  }

  function createDraft(): string {
    const key = newDraftKey();
    const snap = emptySnapshot(key);
    working.value = { ...working.value, [key]: snap };
    return key;
  }

  function patchWorking(draftKey: string, patch: Partial<ProjectCreateDraftSnapshotV1>) {
    if (!draftKey) return;
    const cur = ensureDraft(draftKey);
    const next: ProjectCreateDraftSnapshotV1 = {
      ...cur,
      ...patch,
      form: patch.form ? { ...cur.form, ...patch.form } : cur.form,
      phasePlans: patch.phasePlans ? [...patch.phasePlans] : cur.phasePlans,
      aiProjectRisks: patch.aiProjectRisks ? [...patch.aiProjectRisks] : cur.aiProjectRisks,
      messagesSnapshot:
        patch.messagesSnapshot !== undefined ? [...patch.messagesSnapshot] : cur.messagesSnapshot,
      updatedAt: Date.now(),
    };
    working.value = { ...working.value, [draftKey]: next };
  }

  function tabTitleForDraft(draftKey: string): string {
    const s = getWorking(draftKey) ?? loadPersistedDraft(draftKey);
    const name = (s?.form.name || '').trim();
    return `${name || '新项目'}项目创建`;
  }

  function loadPersistedDraft(draftKey: string): ProjectCreateDraftSnapshotV1 | null {
    if (!draftKey) return null;
    try {
      const raw = localStorage.getItem(LS_PREFIX + draftKey);
      if (!raw) return null;
      const o = JSON.parse(raw) as ProjectCreateDraftSnapshotV1;
      if (!o || o.version !== 1 || o.draftKey !== draftKey) return null;
      return o;
    } catch {
      return null;
    }
  }

  /** 静默自动保存（仅本地 JSON，不含 Word HTML） */
  function autosaveLocal(draftKey: string) {
    const s = getWorking(draftKey);
    if (!s) return;
    try {
      const { wordSnapshotHtml: _w, ...rest } = s;
      localStorage.setItem(LS_PREFIX + draftKey, JSON.stringify(rest));
      if (s.sessionId) {
        upsertIndexRow({
          draftKey,
          sessionId: s.sessionId,
          updatedAt: s.updatedAt,
          titleHint: (s.form.name || '新项目').trim(),
        });
      }
    } catch {
      /* quota */
    }
  }

  function getDraftKeyBySessionId(sessionId: string): string | null {
    if (!sessionId?.trim()) return null;
    const row = readIndex().find((r) => r.sessionId === sessionId.trim());
    return row?.draftKey ?? null;
  }

  /** 索引行缺失时，扫描 localStorage 草稿 JSON 中的 sessionId（与历史会话对齐） */
  function findDraftKeyBySessionIdStorageScan(sessionId: string): string | null {
    const sid = String(sessionId ?? '').trim();
    if (!sid) return null;
    try {
      for (let i = 0; i < localStorage.length; i += 1) {
        const k = localStorage.key(i);
        if (!k || !k.startsWith(LS_PREFIX)) continue;
        const raw = localStorage.getItem(k);
        if (!raw) continue;
        let o: { sessionId?: string; draftKey?: string };
        try {
          o = JSON.parse(raw) as { sessionId?: string; draftKey?: string };
        } catch {
          continue;
        }
        if (o?.sessionId && String(o.sessionId).trim() === sid) {
          const dk = String(o.draftKey ?? '').trim();
          if (dk) return dk;
          const suffix = k.slice(LS_PREFIX.length);
          return suffix || null;
        }
      }
    } catch {
      /* ignore */
    }
    return null;
  }

  /** 用户确认保存：本地 JSON + Word HTML + 将会话写入后端（便于历史记录找回） */
  async function persistDraftFull(draftKey: string) {
    const s = ensureDraft(draftKey);
    const title = `${(s.form.name || '新项目').trim()}项目创建`;
    const savedAtIso = new Date().toISOString();
    const wordHtml = buildProjectCreateWordSnapshotHtml({
      projectTitle: s.form.name || '新项目',
      form: s.form,
      phasePlans: s.phasePlans,
      aiProjectGoal: s.aiProjectGoal,
      aiProjectType: s.aiProjectType,
      aiProjectRisks: s.aiProjectRisks,
      aiSuggestedRiskLevel: s.aiSuggestedRiskLevel,
      savedAtIso,
    });
    const next: ProjectCreateDraftSnapshotV1 = { ...s, wordSnapshotHtml: wordHtml, updatedAt: Date.now() };
    working.value = { ...working.value, [draftKey]: next };
    try {
      localStorage.setItem(LS_PREFIX + draftKey, JSON.stringify(next));
      if (next.sessionId) {
        upsertIndexRow({
          draftKey,
          sessionId: next.sessionId,
          updatedAt: next.updatedAt,
          titleHint: (next.form.name || '新项目').trim(),
        });
      }
    } catch {
      /* ignore */
    }
    const messages = s.messagesSnapshot ?? [];
    if (messages.length && next.sessionId) {
      try {
        await saveAgentConversation({
          scenario: 'project_assistance',
          sessionId: next.sessionId,
          routeName: 'ProjectCreate',
          title,
          messages,
        });
      } catch (e) {
        console.error('保存创建项目会话失败', e);
      }
    }
  }

  function removeDraftLocal(draftKey: string) {
    try {
      localStorage.removeItem(LS_PREFIX + draftKey);
    } catch {
      /* ignore */
    }
    const nextW = { ...working.value };
    delete nextW[draftKey];
    working.value = nextW;
    const idx = readIndex().filter((r) => r.draftKey !== draftKey);
    writeIndex(idx);
  }

  /** 将内存快照预置为从本地恢复（打开历史页签时用） */
  function hydrateWorkingFromStorage(draftKey: string): boolean {
    const loaded = loadPersistedDraft(draftKey);
    if (!loaded) return false;
    working.value = { ...working.value, [draftKey]: loaded };
    return true;
  }

  return {
    working,
    createDraft,
    ensureDraft,
    getWorking,
    patchWorking,
    tabTitleForDraft,
    autosaveLocal,
    persistDraftFull,
    removeDraftLocal,
    getDraftKeyBySessionId,
    findDraftKeyBySessionIdStorageScan,
    hydrateWorkingFromStorage,
    loadPersistedDraft,
  };
});
